/**
 * Push notification delivery card.
 *
 * Backend shape:
 *   {
 *     status: 'ok' | 'unavailable',
 *     error: string | null,
 *     sent_24h: number,
 *     failed_24h: number,
 *     failure_rate_pct: number | null,
 *   }
 *
 * failure_rate_pct is the share of ATTEMPTS that failed — failed / (sent +
 * failed). It is null only when nothing was attempted in the window.
 *
 * 2026-08-21 (U29): this card could not go amber. The backend queried
 * UDC/Application PushNotification.* with no dimensions, which matches
 * nothing, so sent_24h was always 0; the old rate divided by sent, so the
 * rate was always null, so the border stayed neutral and the figure stayed an
 * em-dash. On 2026-08-11 PROD sent 1 push and failed 28 — 96.6% of attempts,
 * every one of them reason=InvalidCredentials — and this card looked calm.
 */
import { formatNumber } from '@/lib/utils';
import { TileMeta, TileUnavailable, isUnavailable, tileShell } from './TileStatus';

interface Data extends TileMeta {
  sent_24h: number;
  failed_24h: number;
  failure_rate_pct: number | null;
}

const FAILURE_ALARM_PCT = 1.0;

export function PushDeliveryCard({ data }: { data: Data }) {
  const unavailable = isUnavailable(data);
  const rate = data?.failure_rate_pct;
  const elevated = !unavailable && rate != null && rate >= FAILURE_ALARM_PCT;
  return (
    <section
      className={`border rounded-lg p-6 ${tileShell(
        unavailable,
        elevated ? 'border-amber-500/50 bg-amber-500/5' : 'bg-card',
      )}`}
    >
      <h3 className="text-lg font-semibold mb-3">Push delivery (last 24h)</h3>
      {unavailable && <TileUnavailable error={data?.error} />}
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Sent</div>
          <div className="text-2xl font-semibold">
            {unavailable ? '—' : formatNumber(data?.sent_24h ?? 0)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Failed</div>
          <div
            className={`text-2xl font-semibold ${
              !unavailable && (data?.failed_24h ?? 0) > 0 ? 'text-amber-700' : ''
            }`}
          >
            {unavailable ? '—' : formatNumber(data?.failed_24h ?? 0)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Failure rate</div>
          <div className={`text-2xl font-semibold ${elevated ? 'text-amber-700' : ''}`}>
            {unavailable ? '—' : rate == null ? 'no sends' : `${rate.toFixed(2)}%`}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            share of attempts · alarm at ≥ {FAILURE_ALARM_PCT}%
          </div>
        </div>
      </div>
    </section>
  );
}
